import {Icon} from '@wandb/weave/components/Icon';
import React, {useCallback, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  DEPRECATED_WORKSPACE_PLACEHOLDER_NAME,
  WORKSPACE_PLACEHOLDER_NAME,
} from '../../../state/workspaces/displayNames';
import {DEFAULT_WORKSPACE_DISPLAY_NAME} from '../../../state/workspaces/types';
import {type WorkspaceViews} from '../../../state/workspaces/workspaceViewListSelector';
import {useViewBarContext} from '../NamedWorkspaceViewBarContext';
import {isUnnamedWorkspace} from '../utils';
import {
  DEFAULT_MAX_NUM_CHARS,
  EditableWorkspaceName,
} from './EditableWorkspaceName';

export const NamedWorkspaceLabel = ({
  displayName,
  wokspaceDisplayNameRef,
  isRenameAllowed,
  setIsRenaming,
}: {
  displayName: string;
  wokspaceDisplayNameRef: React.MutableRefObject<HTMLInputElement | null>;
  isRenameAllowed: boolean;
  setIsRenaming: (isRenaming: boolean) => void;
}) => {
  return (
    <h2
      data-test="nw-label"
      className={twMerge(
        'ml-4 mr-8 text-xl font-semibold',
        'truncate',
        isRenameAllowed
          ? 'hover:cursor-text hover:bg-oblivion/[.05]'
          : 'cursor-default',
        isUnnamedWorkspace(displayName)
          ? 'text-wb-text-disabled'
          : 'text-wb-text-primary'
      )}
      ref={wokspaceDisplayNameRef}
      onClick={() => {
        if (isRenameAllowed) {
          setIsRenaming(true);
        }
      }}>
      {displayName}
    </h2>
  );
};

export const EditableNamedWorkspaceLabel = ({
  initialInputWidth,
  setIsRenaming,
}: {
  initialInputWidth: number | undefined;
  setIsRenaming: (isRenaming: boolean) => void;
}) => {
  const [error, setError] = useState<string | undefined>();
  const {displayName, renameWorkspace, sharedViews, currentWorkspaceViewRef} =
    useViewBarContext();

  const checkIsValid = useCallback(
    (newValue: string) => {
      const {isValid, errorMsg} = getIsValidWorkspaceName(
        currentWorkspaceViewRef.id,
        newValue,
        sharedViews
      );
      if (!isValid && errorMsg.length > 0) {
        setError(errorMsg);
      }

      return isValid;
    },
    [sharedViews, currentWorkspaceViewRef.id]
  );

  // TODO - handle when rename workspace errors out
  const onSave = useCallback(
    (newValue: string) => {
      renameWorkspace?.(newValue);
    },
    [renameWorkspace]
  );

  const isUnnamed = isUnnamedWorkspace(displayName);
  const value = isUnnamed ? '' : displayName;

  return (
    <div className={twMerge('flex w-full items-center', 'ml-10 mr-16')}>
      <EditableWorkspaceName
        value={value}
        isDisabled={renameWorkspace == null}
        error={error}
        setError={setError}
        checkIsValid={checkIsValid}
        onSave={onSave}
        onBlur={() => setIsRenaming(false)}
        placeholder={WORKSPACE_PLACEHOLDER_NAME}
        initialInputWidth={
          displayName === WORKSPACE_PLACEHOLDER_NAME ? 170 : initialInputWidth
        }
      />
      <div className="ml-8 flex gap-8">
        {!error && (
          <p className="rounded bg-wb-surface-sunken px-6 text-sm text-wb-text-tertiary">
            Enter
          </p>
        )}
        {error && (
          <div className="flex h-32 items-center gap-8 rounded bg-red-300/[0.24] px-8 text-red-600">
            <Icon height={18} widht={18} name="warning-alt" />
            <p className="text-sm leading-[28px]">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export function getIsValidWorkspaceName(
  currViewCid: string,
  value: string,
  sharedViews: WorkspaceViews
) {
  if (value === '') {
    return {isValid: false, errorMsg: ''};
  }

  if (
    value === DEPRECATED_WORKSPACE_PLACEHOLDER_NAME ||
    value === WORKSPACE_PLACEHOLDER_NAME ||
    value === DEFAULT_WORKSPACE_DISPLAY_NAME
  ) {
    return {isValid: false, errorMsg: 'This is a reserved name.'};
  }

  if (value.length > DEFAULT_MAX_NUM_CHARS) {
    return {isValid: false, errorMsg: 'Max character length is 40.'};
  }

  const isDuplicateName = Object.values(sharedViews).some(
    item =>
      item.cid !== currViewCid &&
      item.displayName.toLowerCase() === value.toLowerCase()
  );

  if (isDuplicateName) {
    return {
      isValid: false,
      errorMsg: 'Another view already uses this name.',
    };
  }

  return {isValid: true, errorMsg: ''};
}
